import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import VideocamIcon from '@material-ui/icons/Videocam';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import LibraryMusicIcon from '@material-ui/icons/LibraryMusic';
import cn from 'classnames';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { useTranslation } from '../../src/i18n';
import { colors } from '../../constants/styleConstants';
import { enqueueSnackbarAction } from '../../containers/App/actions';
import { FILE_TYPE, TP } from '../../constants/consts';
import { getAcceptFormatsTypesForFiles } from '../../utils/editCommon';

const useStyles = makeStyles(() => ({
  drop: {
    border: '1px dashed rgba(151, 151, 151, 0.53)',
    padding: 25,
    color: colors.orange,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minHeight: 136,
    backgroundColor: colors.white,
  },
  title: {
    fontFamily: 'Roboto',
    fontSize: 15,
    fontWeight: 'bold',
    letterSpacing: 0,
    color: '#3a3a3a',
  },
  subTitle: {
    fontFamily: 'Roboto',
    fontSize: 15,
    letterSpacing: 0,
    color: '#3a3a3a',
    margin: 0,
  },
  dragging: {
    backgroundColor: 'rgba(253, 124, 40, 0.1)',
    display: 'flex',
    alignContent: 'center',
    fontFamily: 'Roboto',
    fontSize: 22,
    color: '#3a3a3a',
  },
  dropText: {
    margin: 'auto',
  },
}));

const FieldProperties = {
  file: {
    title: 'Files',
    icon: <InsertDriveFileIcon />,
    accept: [
      FILE_TYPE.PDF,
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ],
  },
  video: {
    title: 'Video',
    icon: <VideocamIcon />,
    accept: ['video/*'],
  },
  audio: {
    title: 'Audio',
    icon: <LibraryMusicIcon />,
    accept: [], // To be filled when implementing this part
  },
  image: {
    title: `${TP}.m_IMAGES`,
    icon: <AddAPhotoIcon />,
    accept: [FILE_TYPE.PNG, FILE_TYPE.JPEG],
  },
  doc: {
    title: 'Files',
    icon: <InsertDriveFileIcon />,
    accept: [FILE_TYPE.PNG, FILE_TYPE.JPEG, FILE_TYPE.PDF],
  },
};

const DropzoneInput = ({ className, onFileUpload, title, subtitle, type = 'file', accept, multiple }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const acceptedFormats = accept || FieldProperties[type].accept;
  const acceptForInput = accept
    ? getAcceptFormatsTypesForFiles(type, acceptedFormats)
    : FieldProperties[type].accept?.join(', ');

  const onDrop = useCallback(
    acceptedFiles => {
      const toSend = [];
      acceptedFiles.forEach(file => {
        if (acceptedFormats.some(el => new RegExp(el).test(file.type))) {
          toSend.push({ filename: file?.name, size: file?.size, type: file.type, file });
        } else {
          dispatch(
            enqueueSnackbarAction({
              message: `${file.type} is not an accepted filetype.`,
              options: {
                variant: 'error',
              },
            }),
          );
        }
      });
      onFileUpload(toSend);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFileUpload, type],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple });

  return (
    <div {...getRootProps()} className={cn(classes.drop, className, { [classes.dragging]: isDragActive })}>
      <input {...getInputProps()} accept={acceptForInput} />
      {isDragActive ? (
        <div className={classes.dropText}>
          {t(`${TP}.FN_UPLOAD_FILE_DROP_TEMPLATE`, {
            type: t(FieldProperties[type].title),
          })}
        </div>
      ) : (
        <div>
          {FieldProperties[type].icon}
          <h4 className={classes.title}>
            {title || t(`${TP}.FN_UPLOAD_FILE_TEMPLATE`, { type: t(FieldProperties[type].title) })}
          </h4>
          <p className={classes.subTitle}>
            {subtitle ||
              t(`${TP}.FN_UPLOAD_FILE_DESCRIPTION_TEMPLATE`, {
                type: t(FieldProperties[type].title),
              })}
          </p>
        </div>
      )}
    </div>
  );
};

DropzoneInput.propTypes = {
  className: PropTypes.string,
  type: PropTypes.oneOf(['file', 'video', 'audio', 'image', 'doc']),
  title: PropTypes.node,
  subtitle: PropTypes.node,
  onFileUpload: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
};

DropzoneInput.defaultProps = {
  multiple: true,
  type: 'file',
};

export default DropzoneInput;
